import { Text, deepMerge } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useQuery } from '@tanstack/react-query';
import { OnChangeFn, RowSelectionState } from '@tanstack/table-core';
import dayjs from 'dayjs';
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowSelectionState,
  MRT_TableOptions,
} from 'mantine-react-table';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDate, useFiltersTableOptions } from '@data-access';
import {
  useOrganisations,
  Organization,
  useDeleteOrganisation,
} from '@organisations/data-access';
import { PfTable, PfTableProps, useRowSelection } from '@ui';

/* eslint-disable-next-line */
export interface OrganisationsTableProps {
  options?: Partial<MRT_TableOptions<Organization>>;
  withActions?: boolean;
  rowSelection?: MRT_RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
}

export function OrganisationsTable({
  options = {},
  withActions = true,
  rowSelection,
  setRowSelection,
}: OrganisationsTableProps) {
  const { t } = useTranslation();
  const filtersTableOptions =
    useFiltersTableOptions<Organization>('organisations');
  const organisations = useOrganisations();
  const deleteOrganisation = useDeleteOrganisation();

  const rowSelectionProps = useRowSelection<Organization>({
    data: organisations.data?.results || [],
    rowSelection,
    setRowSelection,
  });

  const tableColumns = useMemo<MRT_ColumnDef<Organization>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('common.properties.organization'),
        accessorFn: (row) => `${row.name}`,
      },
      {
        accessorKey: 'created_at',
        Cell: ({ cell }) => {
          const date = cell.getValue() as string;
          return formatDate({
            date,
            format: 'display-hour',
          });
        },
        header: t('common.properties.date_joined'),
        filterVariant: 'date-range',
      },
    ],
    [t],
  );

  const baseOptions = deepMerge<MRT_TableOptions<Organization>>(
    {
      columns: tableColumns,
      data: organisations.data?.results || [],

      rowCount: organisations.data?.count || 0,

      state: {
        rowSelection,
        showLoadingOverlay: organisations.isLoading,
        showProgressBars:
          organisations.isFetching || deleteOrganisation.isPending,
      },

      initialState: {
        showColumnFilters: true,
      },
      mantinePaperProps: {
        h: '100%',
      },
      enableTableFooter: true,
      ...rowSelectionProps,
    },
    options,
  );
  const tableOptions = deepMerge<MRT_TableOptions<Organization>>(
    baseOptions,
    filtersTableOptions,
  );

  const handleDelete = useCallback(
    (row: MRT_Row<Organization>) => {
      modals.openConfirmModal({
        title: t('organisations.delete_title'),

        children: (
          <Text>
            {t('organisations.confirm_delete')}{' '}
            <Text fw={700} size="lg" span>
              {row.original.name}
            </Text>{' '}
            ? {t('common.permanent_action')}
          </Text>
        ),

        labels: { confirm: t('common.delete'), cancel: t('common.cancel') },

        confirmProps: { color: 'red' },

        onConfirm: () => {
          deleteOrganisation.mutate(row.original.id);
        },
      });
    },
    [deleteOrganisation, t],
  );

  const editActionLink: PfTableProps<Organization>['editActionLink'] = (
    row,
  ) => {
    return {
      to: '/organisations/$organisationId/edit',
      params: { organisationId: row.original.id.toString() },
    };
  };

  return (
    <PfTable<Organization>
      editActionLink={withActions ? editActionLink : undefined}
      onDeleteActionClick={withActions ? handleDelete : undefined}
      options={tableOptions}
    />
  );
}

export default OrganisationsTable;
